/**
 * Media section types + local enums.
 *
 * The Cars `media` schema stores lowercase slugs (the column defaults are
 * 'indoor' / 'influencer' / 'not_contacted' / 'pending'), so `value` here is the
 * stored slug and `label` is what the UI renders. Anything not in these lists is
 * surfaced raw rather than hidden — an imported or hand-typed value must never
 * make its row unreachable.
 */

export type Tone = 'slate' | 'sky' | 'violet' | 'amber' | 'rose' | 'emerald';

export interface MediaLookup {
  key: string;
  label: string;
  color: string | null;
  is_active: boolean;
  sort_order: number | null;
}

export type MediaGoal = MediaLookup;
export type MediaFormat = MediaLookup;

export type LookupTable = 'goals' | 'formats';

export interface MediaIdea {
  id: string;
  title: string | null;
  content: string | null;
  reference_url: string | null;
  category: string | null;
  format_key: string | null;
  goal_key: string | null;
  posted: boolean;
  is_approved: boolean;
  note: string | null;
  converted_post_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface MediaPost {
  id: string;
  post_date: string | null;
  /** Generated by the DB — read-only. */
  week_no: number | null;
  week_label: string | null;
  goal_key: string | null;
  format_key: string | null;
  objective: string | null;
  visual_script: string | null;
  caption: string | null;
  cta: string | null;
  media_link: string | null;
  reference_url: string | null;
  posted: boolean;
  source_idea_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface MediaInfluencer {
  id: string;
  name: string | null;
  followers_count: string | null;
  url: string | null;
  email_contact: string | null;
  type: string | null;
  country: string | null;
  notes: string | null;
  messaging_status: string | null;
  final_decision: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface EnumOption {
  value: string;
  label: string;
  tone: Tone;
}

export const IDEA_CATEGORIES: readonly EnumOption[] = [
  { value: 'indoor', label: 'Indoor', tone: 'sky' },
  { value: 'outdoor', label: 'Outdoor', tone: 'emerald' },
  { value: 'graphic', label: 'Graphic', tone: 'violet' },
  { value: 'reels', label: 'Reels', tone: 'rose' },
  { value: 'ugc', label: 'UGC', tone: 'amber' },
  { value: 'trending', label: 'Trending', tone: 'slate' },
];

export const INFLUENCER_TYPES: readonly EnumOption[] = [
  { value: 'popular_influencer', label: 'Popular Influencer', tone: 'violet' },
  { value: 'influencer', label: 'Influencer', tone: 'sky' },
  { value: 'ugc', label: 'UGC', tone: 'amber' },
];

export const MESSAGING_STATUSES: readonly EnumOption[] = [
  { value: 'not_contacted', label: 'Not Contacted', tone: 'slate' },
  { value: 'contacted', label: 'Contacted', tone: 'sky' },
  { value: 'replied', label: 'Replied', tone: 'violet' },
  { value: 'in_talks', label: 'In Talks', tone: 'amber' },
  { value: 'no_reply', label: 'No Reply', tone: 'rose' },
];

export const FINAL_DECISIONS: readonly EnumOption[] = [
  { value: 'pending', label: 'Pending', tone: 'slate' },
  { value: 'approved', label: 'Approved', tone: 'emerald' },
  { value: 'rejected', label: 'Rejected', tone: 'rose' },
  { value: 'on_hold', label: 'On Hold', tone: 'amber' },
];

/** Label for a stored value, falling back to the raw value so unknowns stay visible. */
export function labelFor(
  options: readonly EnumOption[],
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return options.find((o) => o.value === value)?.label ?? value;
}

export type ActionResult = { ok: boolean; error?: string };
export type SaveResult = ActionResult & { id?: string };
export type ConvertResult = ActionResult & { postId?: string; warning?: string };

/** Fields the admin may write on a post. `week_no` is generated; `id` is the key. */
export type PostInput = {
  id?: string;
  post_date: string | null;
  week_label: string | null;
  goal_key: string | null;
  format_key: string | null;
  objective: string | null;
  visual_script: string | null;
  caption: string | null;
  cta: string | null;
  media_link: string | null;
  reference_url: string | null;
};

export type EditablePostField = Exclude<keyof PostInput, 'id'>;

export const EDITABLE_POST_FIELDS: readonly EditablePostField[] = [
  'post_date',
  'week_label',
  'goal_key',
  'format_key',
  'objective',
  'visual_script',
  'caption',
  'cta',
  'media_link',
  'reference_url',
];

/** Post fields stored as links — normalised rather than merely trimmed on write. */
export const URL_POST_FIELDS: readonly EditablePostField[] = ['reference_url'];

export type IdeaInput = {
  id?: string;
  title: string;
  content: string | null;
  reference_url: string | null;
  category: string | null;
  goal_key: string | null;
  format_key: string | null;
  note: string | null;
};

export type InfluencerInput = {
  id?: string;
  name: string;
  followers_count: string | null;
  url: string | null;
  email_contact: string | null;
  type: string | null;
  country: string | null;
  notes: string | null;
  messaging_status: string | null;
  final_decision: string | null;
};

/**
 * Which lookup dimension colours the chips, rails and legend.
 *
 * `MediaGoal` and `MediaFormat` are structurally identical, so everything
 * downstream takes the resolved row and stays blind to which one it got.
 */
export type ColorBy = 'goal' | 'format';
